import { useEffect, useState } from "react";
import { useAuthContext } from "../../contexts/AuthContext";
import { db, auth } from "../../lib/firebase";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import Card from "../../components/Card";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { GraduationCap, LogOut, User, BookOpen, Mail, BarChart3, Lock, CheckCircle, Target, Search, PlayCircle, Menu, X } from "lucide-react";
import AuthModal from "../../components/AuthModal";

interface CourseProgress {
  courseId: string;
  courseTitle: string;
  courseThumbnail: string;
  totalLessons: number;
  completedLessons: number;
  percentage: number;
  lastStudied?: Date;
}

export default function ProgressPage() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const instructorIdFromUrl = searchParams.get('instructorId');
  
  const [courseProgress, setCourseProgress] = useState<CourseProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [instructor, setInstructor] = useState<any>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  const themeColor = instructor?.themeColor || instructor?.buttonColor || "#8B5CF6";
  const headingColor = instructor?.headingColor || "#1F2937";
  const buttonColor = instructor?.buttonColor || instructor?.themeColor || "#8B5CF6";

  console.log('ProgressPage - Instructor:', instructor);
  console.log('ProgressPage - Colors:', { themeColor, headingColor, buttonColor });

  useEffect(() => {
    async function load() {
      if (!user) return;

      console.log('Loading ProgressPage with instructorId:', instructorIdFromUrl);

      // Load instructor from URL parameter first
      if (instructorIdFromUrl) {
        try {
          console.log('Fetching instructor data for:', instructorIdFromUrl);
          const instructorSnap = await getDoc(doc(db, "users", instructorIdFromUrl));
          if (instructorSnap.exists()) {
            const instructorData = { uid: instructorSnap.id, ...instructorSnap.data() };
            console.log('Instructor data loaded:', instructorData);
            setInstructor(instructorData);
          } else {
            console.error('Instructor not found:', instructorIdFromUrl);
          }
        } catch (error) {
          console.error("Error loading instructor:", error);
        }
      } else {
        console.warn('No instructorId in URL parameter');
      }

      const q = query(
        collection(db, "progress"),
        where("userId", "==", user.uid),
        where("completed", "==", true)
      );

      const snap = await getDocs(q);
      const progressData = snap.docs.map((d) => d.data());
      
      console.log('Progress data loaded:', progressData.length, 'completed lessons');
      console.log('Progress details:', progressData);

      // Group by course
      const courseMap = new Map<string, any[]>();
      progressData.forEach((p) => {
        if (!courseMap.has(p.courseId)) {
          courseMap.set(p.courseId, []);
        }
        courseMap.get(p.courseId)!.push(p);
      });
      
      console.log('Courses with progress:', Array.from(courseMap.keys()));

      // Calculate progress for each course
      const progressList: CourseProgress[] = [];
      
      for (const [courseId, lessons] of courseMap.entries()) {
        try {
          const courseDoc = await getDoc(doc(db, "courses", courseId));
          if (!courseDoc.exists()) continue;

          const courseData = courseDoc.data();
          
          // Query lessons from top-level collection filtered by courseId
          const lessonsQuery = query(
            collection(db, "lessons"),
            where("courseId", "==", courseId)
          );
          const lessonsSnap = await getDocs(lessonsQuery);
          const totalLessons = lessonsSnap.size;
          const completedLessons = lessons.length;
          const percentage = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
          
          console.log(`Course ${courseData.title}: ${completedLessons}/${totalLessons} lessons (${percentage}%)`);

          // Find last studied date
          const lastStudied = lessons
            .map((l) => l.completedAt?.toDate())
            .filter(Boolean)
            .sort((a, b) => b.getTime() - a.getTime())[0];

          progressList.push({
            courseId,
            courseTitle: courseData.title || "ไม่ระบุชื่อ",
            courseThumbnail: courseData.thumbnail || "",
            totalLessons,
            completedLessons,
            percentage,
            lastStudied,
          });
        } catch (error) {
          console.error("Error loading course:", courseId, error);
        }
      }

      // Sort by last studied (most recent first)
      progressList.sort((a, b) => {
        if (!a.lastStudied) return 1;
        if (!b.lastStudied) return -1;
        return b.lastStudied.getTime() - a.lastStudied.getTime();
      });

      setCourseProgress(progressList);
      setLoading(false);
    }

    load();
  }, [user, instructorIdFromUrl]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <Card>
          <div className="text-center py-8">
            <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-textDark mb-2">กรุณาเข้าสู่ระบบ</h2>
            <p className="text-textLight mb-6">เพื่อดูความคืบหน้าการเรียนของคุณ</p>
            <Link to="/login">
              <button className="bg-gradient-primary text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105">
                เข้าสู่ระบบ
              </button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลดความคืบหน้า...</p>
        </div>
      </div>
    );
  }

  const totalCompleted = courseProgress.reduce((sum, c) => sum + c.completedLessons, 0);
  const overallPercentage = courseProgress.length > 0
    ? Math.round(courseProgress.reduce((sum, c) => sum + c.percentage, 0) / courseProgress.length)
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-pink-400/10 to-blue-400/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
      <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-gradient-to-br from-purple-400/5 to-pink-400/5 rounded-full blur-2xl"></div>
      
      {/* Navigation Menu */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-lg relative">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to={instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid || instructorIdFromUrl}`} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              {instructor?.profileImage ? (
                <img src={instructor.profileImage} alt={instructor.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-lg" />
              ) : (
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg" style={{ background: themeColor }}>
                  {instructor?.name?.[0]?.toUpperCase() || "T"}
                </div>
              )}
              <div>
                <h2 className="font-bold text-gray-800 font-sans">{instructor?.name || "ผู้สอน"}</h2>
                <p className="text-xs text-gray-500 font-sans flex items-center gap-1">
                  <GraduationCap className="w-3 h-3" />
                  ผู้สอนมืออาชีพ
                </p>
              </div>
            </Link>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-4">
              {(instructor?.uid || instructorIdFromUrl) && (
                <Link to={instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid || instructorIdFromUrl}`}>
                  <button
                    className="px-4 py-2 rounded-xl font-semibold font-sans transition-all flex items-center gap-2 text-gray-600 hover:text-gray-800"
                  >
                    <BookOpen className="w-4 h-4" />
                    คอร์สทั้งหมด
                  </button>
                </Link>
              )}
              {user && (instructor?.uid || instructorIdFromUrl) && (
                <>
                  <button
                    onClick={() => {
                      // Check if on subdomain or main domain
                      const isSubdomain = window.location.hostname !== 'localhost' && 
                                         window.location.hostname !== '127.0.0.1' && 
                                         !window.location.hostname.startsWith('edumi.co') &&
                                         window.location.hostname.includes('.edumi.co');
                      
                      if (isSubdomain) {
                        // On subdomain, navigate to root with section parameter
                        navigate('/?section=my');
                      } else {
                        // On main domain, navigate to instructor profile
                        const baseUrl = instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid || instructorIdFromUrl}`;
                        navigate(`${baseUrl}?section=my`);
                      }
                    }}
                    className="px-4 py-2 rounded-xl font-semibold font-sans transition-all flex items-center gap-2 text-gray-600 hover:text-gray-800"
                  >
                    <User className="w-4 h-4" />
                    คอร์สของฉัน
                  </button>
                  <button
                    onClick={() => {
                      // Check if on subdomain or main domain
                      const isSubdomain = window.location.hostname !== 'localhost' && 
                                         window.location.hostname !== '127.0.0.1' && 
                                         !window.location.hostname.startsWith('edumi.co') &&
                                         window.location.hostname.includes('.edumi.co');
                      
                      if (isSubdomain) {
                        // On subdomain, navigate to root with section parameter
                        navigate('/?section=history');
                      } else {
                        // On main domain, navigate to instructor profile
                        const baseUrl = instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid || instructorIdFromUrl}`;
                        navigate(`${baseUrl}?section=history`);
                      }
                    }}
                    className="px-4 py-2 rounded-xl font-semibold font-sans transition-all flex items-center gap-2 text-gray-600 hover:text-gray-800"
                  >
                    <Mail className="w-4 h-4" />
                    ประวัติการชำระเงิน
                  </button>
                </>
              )}
              {user ? (
                <button
                  onClick={() => auth.signOut()}
                  className="px-4 py-2 bg-red-50 hover:bg-red-100 rounded-xl font-semibold font-sans text-red-600 transition-all flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  ออกจากระบบ
                </button>
              ) : (
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="px-6 py-2 text-white rounded-xl font-semibold font-sans shadow-lg hover:shadow-xl transition-all transform hover:scale-105 flex items-center gap-2"
                  style={{ background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)` }}
                >
                  <User className="w-4 h-4" />
                  เข้าสู่ระบบ
                </button>
              )}
            </div>

            {/* Mobile Hamburger Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {showMobileMenu ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto py-12 px-4 relative z-10">
        {/* Header with Gradient Background */}
        <div className="mb-8 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-3xl blur-xl"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/50">
            <h1 className="text-4xl md:text-5xl font-bold mb-2 font-sans flex items-center gap-3" style={{ color: headingColor }}>
              <div className="p-3 rounded-xl shadow-lg" style={{ background: `linear-gradient(135deg, ${themeColor}20, ${themeColor}10)` }}>
                <BarChart3 className="w-10 h-10" style={{ color: themeColor }} />
              </div>
              ความคืบหน้าการเรียน
            </h1>
            <p className="text-gray-600 font-sans text-lg ml-16">
              ติดตามความก้าวหน้าของคุณในแต่ละคอร์ส
            </p>
          </div>
        </div>

        {/* Stats Overview with Modern Design */}
        {courseProgress.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
              <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all border border-white/50">
                <div className="text-center">
                  <div className="inline-flex p-4 rounded-xl mb-3 shadow-lg" style={{ background: `linear-gradient(135deg, ${themeColor}20, ${themeColor}10)` }}>
                    <BookOpen className="w-12 h-12" style={{ color: themeColor }} />
                  </div>
                  <p className="text-4xl font-bold mb-2" style={{ color: themeColor }}>{courseProgress.length}+</p>
                  <p className="text-sm text-gray-600 font-semibold">คอร์สที่กำลังเรียน</p>
                </div>
              </div>
            </div>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
              <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all border border-white/50">
                <div className="text-center">
                  <div className="inline-flex p-4 rounded-xl mb-3 bg-gradient-to-br from-green-500/20 to-emerald-500/10 shadow-lg">
                    <CheckCircle className="w-12 h-12 text-green-500" />
                  </div>
                  <p className="text-4xl font-bold text-green-500 mb-2">{totalCompleted}</p>
                  <p className="text-sm text-gray-600 font-semibold">บทเรียนที่จบแล้ว</p>
                </div>
              </div>
            </div>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
              <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all border border-white/50">
                <div className="text-center">
                  <div className="inline-flex p-4 rounded-xl mb-3 shadow-lg" style={{ background: `linear-gradient(135deg, ${themeColor}20, ${themeColor}10)` }}>
                    <Target className="w-12 h-12" style={{ color: themeColor }} />
                  </div>
                  <p className="text-4xl font-bold mb-2" style={{ color: themeColor }}>{overallPercentage}%</p>
                  <p className="text-sm text-gray-600 font-semibold">ความคืบหน้าโดยรวม</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty State with Modern Design */}
        {courseProgress.length === 0 && (
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-3xl blur-2xl"></div>
            <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-white/50 overflow-hidden">
              <div className="text-center py-16 px-4">
                <div className="inline-flex p-6 rounded-full mb-6 bg-gradient-to-br from-blue-500/10 to-purple-500/10 shadow-lg">
                  <BookOpen className="w-24 h-24 text-gray-400 animate-bounce" style={{ color: themeColor }} />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: headingColor }}>
                  ยังไม่มีความคืบหน้า
                </h2>
                <p className="text-gray-600 text-lg mb-8 max-w-md mx-auto">
                  เริ่มเรียนคอร์สแรกของคุณเพื่อติดตามความคืบหน้า!
                </p>
                <Link to="/courses">
                  <button className="text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-2xl transition-all transform hover:scale-105 flex items-center gap-2 mx-auto" style={{ background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)` }}>
                    <Search className="w-5 h-5" />
                    เรียกดูคอร์สทั้งหมด
                  </button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Course Progress List with Modern Cards */}
        <div className="space-y-6">
          {courseProgress.map((course) => {
            const isCompleted = course.percentage === 100;

            return (
              <div key={course.courseId} className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
                <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-white/50 overflow-hidden">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Thumbnail */}
                  <div className="md:w-48 flex-shrink-0">
                    <img
                      src={course.courseThumbnail}
                      alt={course.courseTitle}
                      className="w-full h-32 md:h-full object-cover rounded-xl"
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-textDark mb-2">
                          {course.courseTitle}
                        </h3>
                        {course.lastStudied && (
                          <p className="text-sm text-textLight">
                            เรียนล่าสุด: {course.lastStudied.toLocaleDateString('th-TH', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </p>
                        )}
                      </div>
                      {isCompleted && (
                        <div className="bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          <span>เรียนจบแล้ว</span>
                        </div>
                      )}
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-textLight">
                          ความคืบหน้า
                        </span>
                        <span className="text-sm font-bold" style={{ color: themeColor }}>
                          {course.completedLessons} / {course.totalLessons} บทเรียน ({course.percentage}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${course.percentage}%`,
                            background: isCompleted ? 'linear-gradient(to right, #4ade80, #16a34a)' : themeColor
                          }}
                        ></div>
                      </div>
                    </div>

                    {/* Action Button */}
                    <Link to={`/courses/${course.courseId}/learn/1${instructorIdFromUrl ? `?instructorId=${instructorIdFromUrl}` : ''}`}>
                      <button className="text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105 flex items-center gap-2" style={{ background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)` }}>
                        {isCompleted ? (
                          <>
                            <BookOpen className="w-5 h-5" />
                            <span>ทบทวนอีกครั้ง</span>
                          </>
                        ) : (
                          <>
                            <PlayCircle className="w-5 h-5" />
                            <span>เรียนต่อ</span>
                          </>
                        )}
                      </button>
                    </Link>
                  </div>
                </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer with Modern Design */}
      <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-12 mt-16 overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-6xl mx-auto px-4 text-center relative z-10">
          <div className="mb-6">
            <h3 className="text-2xl md:text-3xl font-bold mb-2 font-sans bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              {instructor?.name || "ผู้สอน"}
            </h3>
            <p className="text-gray-400 font-sans text-lg">แพลตฟอร์มเรียนออนไลน์คุณภาพ</p>
          </div>
          <div className="border-t border-gray-700/50 pt-6">
            <p className="text-gray-400 text-sm font-sans">
              Powered by <a href="https://coursedee.web.app" target="_blank" rel="noopener noreferrer" className="font-bold hover:underline transition-all" style={{ color: themeColor }}>EduMi</a> - แพลตฟอร์มสร้างคอร์สออนไลน์
            </p>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMobileMenu(false)}></div>
          <div className="absolute top-0 left-0 right-0 bg-white shadow-2xl animate-slideDown">
            <div className="p-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  {instructor?.profileImage ? (
                    <img src={instructor.profileImage} alt={instructor.name} className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl" style={{ background: themeColor }}>
                      {instructor?.name?.[0]?.toUpperCase() || "T"}
                    </div>
                  )}
                  <div>
                    <h2 className="font-bold text-gray-800">{instructor?.name || "ผู้สอน"}</h2>
                    <p className="text-xs text-gray-500">ผู้สอนมืออาชีพ</p>
                  </div>
                </div>
                <button onClick={() => setShowMobileMenu(false)} className="p-2">
                  <X className="w-6 h-6 text-gray-700" />
                </button>
              </div>

              {/* Menu Items */}
              {(instructor?.uid || instructorIdFromUrl) && (
                <Link
                  to={instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid || instructorIdFromUrl}`}
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors mb-2"
                >
                  <BookOpen className="w-5 h-5 text-gray-600" />
                  <span className="font-semibold text-gray-700">คอร์สทั้งหมด</span>
                </Link>
              )}

              {user && (instructor?.uid || instructorIdFromUrl) && (
                <>
                  <button
                    onClick={() => {
                      // Check if on subdomain or main domain
                      const isSubdomain = window.location.hostname !== 'localhost' && 
                                         window.location.hostname !== '127.0.0.1' && 
                                         !window.location.hostname.startsWith('edumi.co') &&
                                         window.location.hostname.includes('.edumi.co');
                      
                      if (isSubdomain) {
                        // On subdomain, navigate to root with section parameter
                        navigate('/?section=my');
                      } else {
                        // On main domain, navigate to instructor profile
                        const baseUrl = instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid || instructorIdFromUrl}`;
                        navigate(`${baseUrl}?section=my`);
                      }
                      setShowMobileMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors mb-2"
                  >
                    <User className="w-5 h-5 text-gray-600" />
                    <span className="font-semibold text-gray-700">คอร์สของฉัน</span>
                  </button>

                  <button
                    onClick={() => {
                      // Check if on subdomain or main domain
                      const isSubdomain = window.location.hostname !== 'localhost' && 
                                         window.location.hostname !== '127.0.0.1' && 
                                         !window.location.hostname.startsWith('edumi.co') &&
                                         window.location.hostname.includes('.edumi.co');
                      
                      if (isSubdomain) {
                        // On subdomain, navigate to root with section parameter
                        navigate('/?section=history');
                      } else {
                        // On main domain, navigate to instructor profile
                        const baseUrl = instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid || instructorIdFromUrl}`;
                        navigate(`${baseUrl}?section=history`);
                      }
                      setShowMobileMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors mb-2"
                  >
                    <Mail className="w-5 h-5 text-gray-600" />
                    <span className="font-semibold text-gray-700">ประวัติการชำระเงิน</span>
                  </button>
                </>
              )}

              {user ? (
                <button
                  onClick={() => {
                    auth.signOut();
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-semibold">ออกจากระบบ</span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    setShowLoginModal(true);
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-white"
                  style={{ background: `linear-gradient(135deg, ${buttonColor} 0%, ${buttonColor}dd 100%)` }}
                >
                  <User className="w-5 h-5" />
                  <span className="font-semibold">เข้าสู่ระบบ</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)}
        themeColor={themeColor}
      />
    </div>
  );
}
